import React, { useCallback, useEffect, useState } from "react";
import { Container, Button, Row, Col, Form } from "react-bootstrap";
import { Box } from "@mui/material";
import { DataGrid, GridToolbar, gridClasses } from "@mui/x-data-grid";
import Select from "react-select";
import moment from "moment";

import reportApi from "../../services/reportapi";
import branchApi from "../../services/branchapi";
import Loader from "../loader/loader";
import DynamicBreadcrumb from "../header/breadcrumb";
import constantType from "../constantType";

const OnlineOrdersReport = () => {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalRecords, setTotalRecords] = useState(0);
    const [branches, setBranches] = useState([]);
    const [filterModel, setFilterModel] = useState({ items: [] });
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });

    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const formatDate = (date) => date.toISOString().slice(0, 10);

    const [filters, setFilters] = useState({
        fromDate: formatDate(firstDay),
        toDate: formatDate(lastDay),
        branchIds: [],
        placedTypes: [],
        refundedOnly: false
    });

    const PLACED_TYPE_OPTIONS = [
        { value: 1, label: "Web" },
        { value: 3, label: "iOS" },
        { value: 4, label: "Android" },
    ];

    const branchOptions = branches.map((b) => ({
        value: Number(b.branchId),
        label: b.branchName,
    }));

    const updateFilters = (key, value) => {
        setFilters((prev) => ({ ...prev, [key]: value }));
    };

    useEffect(() => {
        const fetchBranches = async () => {
            try {
                const branchData = await branchApi.getAllBranches({
                    limit: 200,
                    page: 0,
                    status: 1,
                    sortModel: [{ field: "branchName", sort: "asc" }],
                });
                setBranches(branchData?.data?.result || []);
            } catch (e) {
                console.error("Error fetching branches:", e);
            }
        };
        fetchBranches();
    }, []);

    const fetchReport = useCallback(async () => {
        setLoading(true);
        try {
            const { page, pageSize } = paginationModel;
            const search = filterModel?.quickFilterValues?.[0] ?? "";

            const res = await reportApi.getOnlineOrdersReport({
                limit: pageSize,
                page,
                search,
                fromDate: filters.fromDate || null,
                toDate: filters.toDate || null,

                branchIds: filters.branchIds?.length ? filters.branchIds : null,
                placedTypes: filters.placedTypes?.length ? filters.placedTypes : null,

                refundedOnly: !!filters.refundedOnly,
            });
            const payload = res?.data?.data || res?.data || {};
            const list = payload?.result || [];

            const formatted = list.map((r, idx) => ({
                id: r.orderId,
                sno: idx + 1 + page * pageSize,
                orderShowId: r.orderShowId,
                createdAtFmt: r.createdAt ? moment(r.createdAt).format("YYYY-MM-DD HH:mm") : "-",
                branchName: r.branchName || "-",
                deliveryTypeText: constantType.deliveryTypes?.[r.deliveryType] || String(r.deliveryType || "-"),

                shippingCharge: Number(r.shippingCharge ?? 0),
                grossSale: Number(r.grossSale ?? 0),
                paidAmount: Number(r.paidAmount ?? 0),
                refundAmount: Number(r.refundAmount ?? 0),
                paidAfterRefund: Number(r.paidAfterRefund ?? 0),
                netSale: Number(r.netSale ?? 0),

                customer: r.customer || "-",
            }));

            // console.log("GRID row:", formatted?.[0]); // ✅ check what grid receives
            setRows(formatted);
            setTotalRecords(payload?.totalRecords || 0);

        } catch (e) {
            console.error("Error fetching online orders report:", e);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, filterModel, filters]);


    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const exportExcel = async () => {
        try {
            const search = filterModel?.quickFilterValues?.[0] ?? "";
            const resp = await reportApi.exportOnlineOrdersReport({
                search,
                fromDate: filters.fromDate || null,
                toDate: filters.toDate || null,

                branchIds: filters.branchIds?.length ? filters.branchIds : null,        // ✅
                placedTypes: filters.placedTypes?.length ? filters.placedTypes : null,  // ✅

                refundedOnly: !!filters.refundedOnly,
            });

            const blob = new Blob([resp.data], {
                type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `online_orders_${filters.fromDate || "all"}_to_${filters.toDate || "all"}.xlsx`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error("Export error:", e);
        }
    };

    const moneyCol = (field, header, minWidth = 120) => ({
        field,
        headerName: header,
        flex: 1,
        minWidth,
        headerAlign: "right",
        align: "right",
        renderCell: (params) => {
            const val = Number(params?.row?.[field] ?? 0);
            return val.toFixed(2);
        },
    });


    const columns = [
        { field: "sno", headerName: "S NO", width: 80 },
        { field: "orderShowId", headerName: "Order Show ID", flex: 1, minWidth: 140 },
        { field: "createdAtFmt", headerName: "Created Date", flex: 1, minWidth: 160 },
        { field: "branchName", headerName: "Branch", flex: 1, minWidth: 160 },
        { field: "deliveryTypeText", headerName: "Delivery Type", flex: 1, minWidth: 160 },

        moneyCol("shippingCharge", "Shipping", 110),
        moneyCol("grossSale", "Gross Sale", 120),
        moneyCol("paidAmount", "Paid", 110),
        moneyCol("refundAmount", "Refund", 110),
        moneyCol("paidAfterRefund", "Paid After Refund", 160),
        moneyCol("netSale", "Net Sale", 120),

        { field: "customer", headerName: "Customer (Name + Mobile)", flex: 1.4, minWidth: 220 },
    ];

    const pathArray = [{ name: "Online Orders Report", url: null }];

    return (
        <Container className="mt-4 mb-4">
            <DynamicBreadcrumb pathArray={pathArray} />
            <h5 className="mb-3">Online Orders Report</h5>

            <Form className="mb-3">
                <Row>
                    <Col md={5}>
                        <Form.Label>Branch</Form.Label>
                        <Select
                            isMulti
                            options={branchOptions}
                            value={branchOptions.filter((o) => filters.branchIds.includes(o.value))}
                            onChange={(selected) =>
                                updateFilters("branchIds", (selected || []).map((x) => x.value))
                            }
                            placeholder="All Branches"
                        />
                    </Col>

                    <Col md={5}>
                        <Form.Label>Placed Type</Form.Label>
                        <Select
                            isMulti
                            options={PLACED_TYPE_OPTIONS}
                            value={PLACED_TYPE_OPTIONS.filter((o) => filters.placedTypes.includes(o.value))}
                            onChange={(selected) =>
                                updateFilters("placedTypes", (selected || []).map((x) => x.value))
                            }
                            placeholder="All (Web / iOS / Android)"
                        />
                    </Col>


                    <Col md={2} className="d-flex align-items-end">
                        <Form.Check
                            type="switch"
                            id="refundedOnlySwitch"
                            label="Refunded Only"
                            checked={filters.refundedOnly}
                            onChange={(e) => updateFilters("refundedOnly", e.target.checked)}
                        />
                    </Col>
                </Row>
                <Row className="mt-3 mb-3">
                    <Col md={2}>
                        <Form.Label>From Date</Form.Label>
                        <Form.Control type="date" value={filters.fromDate} onChange={(e) => updateFilters("fromDate", e.target.value)} />
                    </Col>

                    <Col md={2}>
                        <Form.Label>To Date</Form.Label>
                        <Form.Control type="date" value={filters.toDate} onChange={(e) => updateFilters("toDate", e.target.value)} />
                    </Col>

                    <Col md={5} className="d-flex align-items-end justify-content-end">
                        <Button variant="primary" onClick={fetchReport}>Filter</Button>
                        <Button variant="success" className="ms-2" onClick={exportExcel}>Export Excel</Button>
                    </Col>
                </Row>
            </Form>

            {loading && <Loader />}

            <Box>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowHeight={() => "auto"}
                    paginationMode="server"
                    rowCount={totalRecords}
                    pageSizeOptions={[10, 20, 50, 100]}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    filterModel={filterModel}
                    onFilterModelChange={setFilterModel}
                    loading={loading}
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                            csvOptions: { disableToolbarButton: true },
                            printOptions: { disableToolbarButton: true },
                        },
                    }}
                    sx={{ [`& .${gridClasses.cell}`]: { py: 1 } }}
                    initialState={{
                        columns: {
                            columnVisibilityModel: {
                                sno: true,
                                orderShowId: true,
                                createdAtFmt: true,
                                branchName: true,
                                deliveryTypeText: true,
                                shippingCharge: true,
                                grossSale: true,
                                paidAmount: true,
                                refundAmount: true,
                                paidAfterRefund: true,
                                netSale: true,
                                customer: true,
                            },
                        },
                    }}

                />
            </Box>
        </Container>
    );
};

export default OnlineOrdersReport;
